/**
 * Custom rewrite styles settings section (pirx-2gm7r.9).
 *
 * CRUD over the active team's `styles` (spec §4.2, Decision 17). Mirrors
 * `SettingsTeamsSection`'s shape — list/create/edit/delete with the same
 * loading/empty/error rendering — and surfaces the `strippedRules` names
 * the backend's normalization/screening call returned on the most recent
 * save (Decision 11(a)/12). Decision 14's "feature not enabled for your
 * team" path is rendered as a quiet unavailable notice (no scary toast),
 * matching how other flag-gated sections handle a disabled backend feature.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/useAuth";
import { useActiveTeam } from "@/services/teams/useActiveTeam";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  type Style,
  createStyle,
  deleteStyle,
  listStyles,
  updateStyle,
} from "@/services/api-client";
import { ApiError } from "@/services/api/errors";
import { resolveApiErrorMessage } from "@/utils/api-error-message";

/**
 * Mirrors the backend's `canManageTeam` rule (pirx-17ymy.1): a style and the
 * variants referencing it are team resources, so only an owner or admin of the
 * active team may create, edit or delete one. Members still read the list.
 */
function canManageTeamStyles(role: string | undefined): boolean {
  return role === "owner" || role === "admin";
}

interface FormState {
  name: string;
  brandLabel: string;
  rawRules: string;
}

interface StrippedNotice {
  /** Style id that produced the notice; used as the key so a save against
   * a different style dismisses the prior one. */
  styleId: string;
  rules: string[];
}

const EMPTY_FORM: FormState = {
  name: "",
  brandLabel: "",
  rawRules: "",
};

/** True when the API rejected the request because the backend feature is
 * globally off or the team lacks the per-team entitlement (Decision 14).
 * Both surface as "not available right now" rather than a hard error. */
function isUnavailable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 503 || err.status === 403);
}

/** A simple inline editor: keeps `form` synced with the row being edited,
 * and a `submitting` flag for the submit button. */
function StyleForm({
  initial,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  errorText,
  nameTestId,
  brandTestId,
  rulesTestId,
  cancelTestId,
  submitTestId,
  errorTestId,
}: {
  initial: FormState;
  busy: boolean;
  submitLabel: string;
  onSubmit: (next: FormState) => void | Promise<void>;
  onCancel?: () => void;
  errorText?: string | null;
  nameTestId: string;
  brandTestId: string;
  rulesTestId: string;
  cancelTestId?: string;
  submitTestId: string;
  errorTestId?: string;
}) {
  const { t } = useTranslation(["settings", "common"]);
  const [form, setForm] = useState<FormState>(initial);

  // Parent passes `key={editingStyle?.id ?? "new"}`, so switching between
  // create and edit (or between two edited rows) remounts the component and
  // re-initializes `form` from the new `initial`.

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSubmit(form);
  };

  return (
    <form
      className="space-y-3"
      onSubmit={handleSubmit}
      data-test="settings-styles-form"
    >
      <div>
        <label
          className="block text-xs font-medium text-[var(--muted-foreground)]"
          htmlFor={`${nameTestId}-input`}
        >
          {t("settings:styles.nameLabel")}
        </label>
        <input
          id={`${nameTestId}-input`}
          required
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          placeholder={t("settings:styles.namePlaceholder")}
          data-test={nameTestId}
          className="mt-1 w-full rounded-sm border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none"
        />
      </div>
      <div>
        <label
          className="block text-xs font-medium text-[var(--muted-foreground)]"
          htmlFor={`${brandTestId}-input`}
        >
          {t("settings:styles.brandLabelLabel")}
        </label>
        <input
          id={`${brandTestId}-input`}
          value={form.brandLabel}
          onChange={(e) =>
            setForm((s) => ({ ...s, brandLabel: e.target.value }))}
          placeholder={t("settings:styles.brandLabelPlaceholder")}
          data-test={brandTestId}
          className="mt-1 w-full rounded-sm border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none"
        />
      </div>
      <div>
        <label
          className="block text-xs font-medium text-[var(--muted-foreground)]"
          htmlFor={`${rulesTestId}-input`}
        >
          {t("settings:styles.rawRulesLabel")}
        </label>
        <Textarea
          id={`${rulesTestId}-input`}
          required
          value={form.rawRules}
          onChange={(e) =>
            setForm((s) => ({ ...s, rawRules: e.target.value }))}
          placeholder={t("settings:styles.rawRulesPlaceholder")}
          rows={5}
          data-test={rulesTestId}
        />
      </div>
      {errorText && errorTestId && (
        <p
          className="text-sm text-[var(--destructive)]"
          data-test={errorTestId}
        >
          {errorText}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="lg"
          disabled={busy}
          data-test={submitTestId}
        >
          {busy ? t("settings:styles.saving") : submitLabel}
        </Button>
        {onCancel && cancelTestId && (
          <Button
            type="button"
            size="lg"
            variant="outline"
            disabled={busy}
            data-test={cancelTestId}
            onClick={onCancel}
          >
            {t("common:buttons.cancel")}
          </Button>
        )}
      </div>
    </form>
  );
}

function rowToForm(style: Style): FormState {
  return {
    name: style.name,
    brandLabel: style.brandLabel ?? "",
    rawRules: style.rawRules,
  };
}

export default function SettingsStylesSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { profile } = useAuth();
  const { teams, activeTeamId, activeTeam } = useActiveTeam(profile);
  const canManageStyles = canManageTeamStyles(activeTeam?.role);

  const [styles, setStyles] = useState<Style[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [strippedNotice, setStrippedNotice] = useState<StrippedNotice | null>(
    null,
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadStyles = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setUnavailable(false);
    try {
      const result = await listStyles();
      setStyles(result.styles);
    } catch (err) {
      setStyles(null);
      if (isUnavailable(err)) {
        setUnavailable(true);
      } else {
        setLoadError(resolveApiErrorMessage(err, "settings:styles.loadError", t));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!activeTeamId) {
      setStyles(null);
      setUnavailable(false);
      return;
    }
    void loadStyles();
  }, [activeTeamId, loadStyles]);

  if (teams.length === 0) {
    return null;
  }

  const handleCreate = async (form: FormState) => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      const result = await createStyle({
        name: form.name.trim(),
        brandLabel: form.brandLabel.trim() ? form.brandLabel.trim() : null,
        rawRules: form.rawRules,
      });
      setStyles((prev) =>
        prev ? [...prev, result.style] : [result.style]
      );
      setStrippedNotice({
        styleId: result.style.id,
        rules: result.strippedRules,
      });
    } catch (err) {
      setCreateError(resolveApiErrorMessage(err, "settings:styles.saveError", t));
    } finally {
      setCreateBusy(false);
    }
  };

  const handleEdit = async (styleId: string, form: FormState) => {
    setEditBusy(true);
    setEditError(null);
    try {
      const result = await updateStyle(styleId, {
        name: form.name.trim(),
        brandLabel: form.brandLabel.trim() ? form.brandLabel.trim() : null,
        rawRules: form.rawRules,
      });
      setStyles((prev) =>
        (prev ?? []).map((s) => (s.id === styleId ? result.style : s))
      );
      setStrippedNotice({
        styleId: result.style.id,
        rules: result.strippedRules,
      });
      setEditingId(null);
    } catch (err) {
      setEditError(resolveApiErrorMessage(err, "settings:styles.saveError", t));
    } finally {
      setEditBusy(false);
    }
  };

  const handleDelete = async (styleId: string) => {
    if (
      typeof window !== "undefined" &&
      !window.confirm(t("settings:styles.deleteConfirm"))
    ) {
      return;
    }
    setDeletingBusy(true);
    setDeleteError(null);
    setDeletingId(styleId);
    try {
      await deleteStyle(styleId);
      setStyles((prev) => (prev ?? []).filter((s) => s.id !== styleId));
      if (strippedNotice?.styleId === styleId) {
        setStrippedNotice(null);
      }
    } catch (err) {
      setDeleteError(resolveApiErrorMessage(err, "settings:styles.deleteError", t));
    } finally {
      setDeletingBusy(false);
      setDeletingId(null);
    }
  };

  const editingStyle = styles?.find((s) => s.id === editingId) ?? null;

  return (
    <section
      className="space-y-6 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6"
      data-test="settings-styles-section"
    >
      <div>
        <h2 className="text-lg font-semibold text-[var(--foreground)]">
          {t("settings:styles.title")}
        </h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          {t("settings:styles.subtitle")}
        </p>
      </div>

      {!activeTeamId
        ? (
          <p
            className="text-sm text-[var(--muted-foreground)]"
            data-test="settings-styles-select-prompt"
          >
            {t("settings:styles.selectPrompt")}
          </p>
        )
        : unavailable
        ? (
          <p
            className="text-sm text-[var(--muted-foreground)]"
            data-test="settings-styles-unavailable"
          >
            {t("settings:styles.unavailable")}
          </p>
        )
        : loading
        ? (
          <p
            className="text-sm text-[var(--muted-foreground)]"
            data-test="settings-styles-loading"
          >
            {t("common:labels.loading")}
          </p>
        )
        : loadError
        ? (
          <p
            className="text-sm text-[var(--destructive)]"
            data-test="settings-styles-error"
          >
            {loadError}
          </p>
        )
        : (
          <>
            {!canManageStyles && (
              <p
                className="text-sm text-[var(--muted-foreground)]"
                data-test="settings-styles-member-read-only"
              >
                {t("settings:styles.memberReadOnly")}
              </p>
            )}

            {styles !== null && styles.length > 0
              ? (
                <Table data-test="settings-styles-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("settings:styles.colName")}</TableHead>
                      <TableHead>{t("settings:styles.colBrand")}</TableHead>
                      <TableHead>{t("settings:styles.colTone")}</TableHead>
                      <TableHead>{t("settings:styles.colFormality")}</TableHead>
                      {canManageStyles && (
                        <TableHead>{t("settings:styles.colActions")}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {styles.map((s) => {
                      const tone = s.normalizedStyleGuide?.tone;
                      const formality = s.normalizedStyleGuide?.formalityLevel;
                      const isStrippedHere =
                        strippedNotice?.styleId === s.id &&
                        strippedNotice.rules.length > 0;
                      const isDeleting = deletingId === s.id;
                      return (
                        <TableRow
                          key={s.id}
                          data-test="settings-styles-row"
                        >
                          <TableCell>
                            <span className="block text-sm text-[var(--foreground)]">
                              {s.name}
                            </span>
                            {isStrippedHere && (
                              <span
                                className="mt-1 block text-xs text-[var(--muted-foreground)]"
                                data-test="settings-styles-row-stripped"
                              >
                                {t("settings:styles.strippedRulesListLabel")}:{" "}
                                {strippedNotice.rules.join("; ")}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-[var(--muted-foreground)]">
                              {s.brandLabel ?? "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-[var(--muted-foreground)]">
                              {tone ?? t("settings:styles.toneUnknown")}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-[var(--muted-foreground)]">
                              {formality ?? "—"}
                            </span>
                          </TableCell>
                          {canManageStyles && (
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={deletingBusy || !!editingId}
                                onClick={() => setEditingId(s.id)}
                                data-test="settings-styles-row-edit"
                              >
                                {t("settings:styles.editAction")}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                disabled={deletingBusy || !!editingId}
                                loading={isDeleting}
                                onClick={() => void handleDelete(s.id)}
                                data-test="settings-styles-row-delete"
                              >
                                {isDeleting
                                  ? t("settings:styles.deleting")
                                  : t("settings:styles.deleteAction")}
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )
              : (
                <p
                  className="text-sm text-[var(--muted-foreground)]"
                  data-test="settings-styles-empty"
                >
                  {t("settings:styles.emptyState")}
                </p>
              )}

            {deleteError && (
              <p
                className="text-sm text-[var(--destructive)]"
                data-test="settings-styles-delete-error"
              >
                {deleteError}
              </p>
            )}

            {strippedNotice && strippedNotice.rules.length > 0 && (
              <div
                className="space-y-1 text-sm text-[var(--muted-foreground)]"
                data-test="settings-styles-stripped-rules"
              >
                <p>
                  {t("settings:styles.strippedRulesNotice", {
                    count: strippedNotice.rules.length,
                  })}
                </p>
                <ul className="list-disc pl-5">
                  {strippedNotice.rules.map((rule, idx) => (
                    <li
                      key={`${idx}-${rule}`}
                      data-test="settings-styles-stripped-rule"
                    >
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canManageStyles && (
              <div className="border-t border-[var(--border)] pt-4">
                <h3 className="text-sm font-medium text-[var(--foreground)]">
                  {editingStyle
                    ? t("settings:styles.editTitle")
                    : t("settings:styles.createTitle")}
                </h3>
                <div className="mt-3">
                <StyleForm
                  key={editingStyle?.id ?? "new"}
                  initial={editingStyle ? rowToForm(editingStyle) : EMPTY_FORM}
                  busy={editingStyle ? editBusy : createBusy}
                  submitLabel={editingStyle
                    ? t("settings:styles.saveSubmit")
                    : t("settings:styles.createSubmit")}
                  onSubmit={(form) => editingStyle
                    ? handleEdit(editingStyle.id, form)
                    : handleCreate(form)}
                  onCancel={editingStyle ? () => setEditingId(null) : undefined}
                  errorText={editingStyle ? editError : createError}
                  nameTestId={editingStyle
                    ? "settings-styles-edit-name"
                    : "settings-styles-create-name"}
                  brandTestId={editingStyle
                    ? "settings-styles-edit-brand"
                    : "settings-styles-create-brand"}
                  rulesTestId={editingStyle
                    ? "settings-styles-edit-rules"
                    : "settings-styles-create-rules"}
                  cancelTestId={editingStyle
                    ? "settings-styles-edit-cancel"
                    : undefined}
                  submitTestId={editingStyle
                    ? "settings-styles-edit-submit"
                    : "settings-styles-create-submit"}
                  errorTestId={editingStyle
                    ? "settings-styles-edit-error"
                    : "settings-styles-create-error"}
                />
                </div>
              </div>
            )}
          </>
        )}
    </section>
  );
}